import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { DotsThree as DotsIcon } from "@phosphor-icons/react/DotsThree";
import type { Account } from "../partner-templates/types";

export interface PortfolioRowActionMenuProps {
	account: Account;
	suspended?: boolean;
	onOpenAccount?: (id: string) => void;
	onOpenSubscription?: (id: string) => void;
	onCancelSubscription?: (id: string) => void;
	onDelete?: (id: string) => void;
	onEndTrialEarly?: (id: string) => void;
	onRestartTrial?: (id: string) => void;
}

export function PortfolioRowActionMenu({
	account,
	suspended = false,
	onOpenAccount,
	onOpenSubscription,
	onCancelSubscription,
	onDelete,
	onEndTrialEarly,
	onRestartTrial,
}: PortfolioRowActionMenuProps): React.JSX.Element {
	const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

	const close = () => setAnchor(null);
	const isTrial = account.subscription.type === "TRIAL";
	const isPaid = account.subscription.type === "PAID";
	const isClosed = account.subscription.type === "CLOSED";
	const trialExpired =
		account.subscription.type === "TRIAL" &&
		account.subscription.trialExpiresAt !== null &&
		new Date(account.subscription.trialExpiresAt).getTime() <= Date.now();
	// Per spec §4.9 partner can only delete after the subscription is closed
	// (or the trial has been ended). Active paid / active trial / past-due are
	// not deletable — they must be cancelled / ended first.
	const canDelete = account.subscription.type === "CLOSED" || trialExpired;

	const suspendedTip = "Your partner account is suspended";

	return (
		<>
			<IconButton
				size="small"
				onClick={(e) => setAnchor(e.currentTarget)}>
				<DotsIcon size={20} />
			</IconButton>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={close}>
				<MenuItem
					onClick={() => {
						close();
						onOpenAccount?.(account.id);
					}}>
					Client details
				</MenuItem>
				{!isClosed && (
					<MenuItem
						onClick={() => {
							close();
							onOpenSubscription?.(account.id);
						}}>
						Manage subscription
					</MenuItem>
				)}
				{isClosed && (
					<Tooltip
						title={suspended ? suspendedTip : ""}
						placement="left"
						disableHoverListener={!suspended}>
						<span>
							<MenuItem
								disabled={suspended}
								onClick={() => {
									close();
									onRestartTrial?.(account.id);
								}}>
								Start a trial
							</MenuItem>
						</span>
					</Tooltip>
				)}
				{isPaid && (
					<Tooltip
						title={suspended ? suspendedTip : ""}
						placement="left"
						disableHoverListener={!suspended}>
						<span>
							<MenuItem
								disabled={suspended}
								onClick={() => {
									close();
									onCancelSubscription?.(account.id);
								}}>
								Cancel subscription
							</MenuItem>
						</span>
					</Tooltip>
				)}
				{isTrial && (
					<Tooltip
						title={suspended ? suspendedTip : ""}
						placement="left"
						disableHoverListener={!suspended}>
						<span>
							<MenuItem
								disabled={suspended}
								onClick={() => {
									close();
									onEndTrialEarly?.(account.id);
								}}>
								Terminate trial
							</MenuItem>
						</span>
					</Tooltip>
				)}
				{canDelete && (
					<MenuItem
						onClick={() => {
							close();
							onDelete?.(account.id);
						}}>
						Delete account
					</MenuItem>
				)}
			</Menu>
		</>
	);
}
